'use client';

import { useCallback, useEffect, useRef } from 'react';

import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

interface DiceProps {
  value: number | null;
  isRolling: boolean;
}

const DEFAULT_DICE_IMAGES: Record<number, string> = {
  1: '/images/dice/dice-1.svg',
  2: '/images/dice/dice-2.svg',
  3: '/images/dice/dice-3.svg',
  4: '/images/dice/dice-4.svg',
  5: '/images/dice/dice-5.svg',
  6: '/images/dice/dice-6.svg',
};

const DiceFace = ({ value, 'data-face': dataFace }: { value: number; 'data-face': string }) => {
  const imageSrc = DEFAULT_DICE_IMAGES[value];

  return (
    <div
      data-face={dataFace}
      className={css({
        position: 'absolute',
        w: '100%',
        h: '100%',
        borderRadius: 8,
        backfaceVisibility: 'hidden',
        overflow: 'hidden',
      })}
    >
      <img
        src={imageSrc}
        alt={`Dice face ${value}`}
        className={css({
          w: '100%',
          h: '100%',
          objectFit: 'cover',
        })}
      />
    </div>
  );
};

const ISOMETRIC_ROTATION = { x: -30, y: 45, z: 0 };

const TARGET_ROTATIONS: Record<number, { x: number; y: number; z: number }> = {
  1: { x: 0, y: 0, z: 0 },
  2: { x: 0, y: -90, z: 0 },
  3: { x: -90, y: 0, z: 0 },
  4: { x: 90, y: 0, z: 0 },
  5: { x: 0, y: 90, z: 0 },
  6: { x: 180, y: 0, z: 0 },
};

const ROLL_SPEED = 8;
const SETTLE_THRESHOLD = 5;

export const Dice = ({ value, isRolling }: DiceProps) => {
  const diceRef = useRef<HTMLDivElement>(null);
  const cubeRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef({ x: ISOMETRIC_ROTATION.x, y: ISOMETRIC_ROTATION.y, z: ISOMETRIC_ROTATION.z });
  const animationFrameRef = useRef<number | null>(null);
  const isSettlingRef = useRef(false);

  const updateTransform = useCallback(() => {
    if (diceRef.current) {
      const { x, y, z } = rotationRef.current;
      diceRef.current.style.transform = `rotateX(${x}deg) rotateY(${y}deg) rotateZ(${z}deg)`;
    }
  }, []);

  useEffect(() => {
    const updateFaceTransforms = () => {
      if (!cubeRef.current) return;
      const cubeSize = cubeRef.current.offsetWidth;
      const translateZ = cubeSize / 2;

      const faces = cubeRef.current.querySelectorAll<HTMLElement>('[data-face]');
      faces.forEach((face) => {
        const faceType = face.dataset.face;
        switch (faceType) {
          case 'front':
            face.style.transform = `translateZ(${translateZ}px)`;
            break;
          case 'back':
            face.style.transform = `rotateY(180deg) translateZ(${translateZ}px)`;
            break;
          case 'right':
            face.style.transform = `rotateY(90deg) translateZ(${translateZ}px)`;
            break;
          case 'left':
            face.style.transform = `rotateY(-90deg) translateZ(${translateZ}px)`;
            break;
          case 'top':
            face.style.transform = `rotateX(90deg) translateZ(${translateZ}px)`;
            break;
          case 'bottom':
            face.style.transform = `rotateX(-90deg) translateZ(${translateZ}px)`;
            break;
        }
      });
    };

    updateFaceTransforms();

    const resizeObserver = new ResizeObserver(updateFaceTransforms);
    if (cubeRef.current) {
      resizeObserver.observe(cubeRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const normalizeAngle = (angle: number): number => {
    return ((angle % 360) + 360) % 360;
  };

  const shortestRotation = (current: number, target: number): number => {
    const normalizedCurrent = normalizeAngle(current);
    const normalizedTarget = normalizeAngle(target);
    let diff = normalizedTarget - normalizedCurrent;

    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;

    return diff;
  };

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isSettlingRef.current = false;

    if (isRolling) {
      const animate = () => {
        rotationRef.current.x += ROLL_SPEED;
        rotationRef.current.y += ROLL_SPEED * 1.3;
        rotationRef.current.z += ROLL_SPEED * 0.7;
        updateTransform();
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (value !== null) {
      const target = TARGET_ROTATIONS[value];

      const targetX = target.x + 360;
      const targetY = target.y + 360;
      const targetZ = target.z;

      isSettlingRef.current = true;

      const settle = () => {
        if (!isSettlingRef.current) return;

        const diffX = shortestRotation(rotationRef.current.x, targetX);
        const diffY = shortestRotation(rotationRef.current.y, targetY);
        const diffZ = shortestRotation(rotationRef.current.z, targetZ);

        const isClose =
          Math.abs(diffX) < SETTLE_THRESHOLD &&
          Math.abs(diffY) < SETTLE_THRESHOLD &&
          Math.abs(diffZ) < SETTLE_THRESHOLD;

        if (isClose) {
          rotationRef.current.x = normalizeAngle(target.x);
          rotationRef.current.y = normalizeAngle(target.y);
          rotationRef.current.z = normalizeAngle(target.z);
          updateTransform();
          isSettlingRef.current = false;
          return;
        }

        rotationRef.current.x += diffX * 0.15;
        rotationRef.current.y += diffY * 0.15;
        rotationRef.current.z += diffZ * 0.15;
        updateTransform();

        animationFrameRef.current = requestAnimationFrame(settle);
      };

      animationFrameRef.current = requestAnimationFrame(settle);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, isRolling, updateTransform]);

  useEffect(() => {
    updateTransform();
  }, [updateTransform]);

  return (
    <div
      className={center({
        w: '100%',
        h: '100%',
        perspective: '400px',
      })}
    >
      <div
        ref={(el) => {
          diceRef.current = el;
          cubeRef.current = el;
        }}
        className={css({
          position: 'relative',
          w: '75%',
          aspectRatio: '1 / 1',
          transformStyle: 'preserve-3d',
        })}
      >
        <DiceFace value={1} data-face='front' />
        <DiceFace value={6} data-face='back' />
        <DiceFace value={2} data-face='right' />
        <DiceFace value={5} data-face='left' />
        <DiceFace value={3} data-face='top' />
        <DiceFace value={4} data-face='bottom' />
      </div>
    </div>
  );
};
